import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  MessageSquareLock, SlidersHorizontal, ScrollText, LayoutDashboard,
  FileText, UserPlus, Users as UsersIcon, PlugZap, GraduationCap, LogOut, Circle, Gauge,
} from "lucide-react";
import { useAuth } from "../App";
import { Pill, Logo } from "./ui";

const NAV = [
  { to: "my", label: "My Dashboard", icon: Gauge, roles: ["employee"] },
  { to: "chat", label: "Gateway Chat", icon: MessageSquareLock, roles: ["admin", "employee"] },
  { to: "signoff", label: "AI Policy & Training", icon: GraduationCap, roles: ["admin", "employee"] },
  { section: "Administration", roles: ["admin"] },
  { to: "policy", label: "Policy Builder", icon: SlidersHorizontal, roles: ["admin"] },
  { to: "audit", label: "Audit Log", icon: ScrollText, roles: ["admin"] },
  { to: "dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin"] },
  { to: "report", label: "Monthly Report", icon: FileText, roles: ["admin"] },
  { to: "users", label: "Users & Departments", icon: UsersIcon, roles: ["admin"] },
  { to: "onboarding", label: "Onboard Tenant", icon: UserPlus, roles: ["admin"] },
  { to: "integration", label: "Integration Surface", icon: PlugZap, roles: ["admin"] },
];

export default function Shell() {
  const { session, logout } = useAuth();
  const navigate = useNavigate();
  const role = session.user.role;
  const items = NAV.filter((n) => n.roles.includes(role));

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="lg-backdrop flex min-h-screen">
      {/* Sidebar */}
      <aside className="sticky top-0 hidden h-screen w-64 flex-col border-r border-subtle bg-surface/70 backdrop-blur-xl lg:flex">
        <div className="flex items-center px-4 py-5">
          <Logo className="h-11" />
        </div>

        <nav className="custom-scrollbar flex-1 overflow-y-auto px-3 py-2">
          {items.map((n, i) =>
            n.section ? (
              <div key={i} className="px-3 pb-1 pt-5 font-mono text-[10px] uppercase tracking-widest text-slate-600">
                {n.section}
              </div>
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to}`}
                className={({ isActive }) =>
                  `mb-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                    isActive
                      ? "bg-brand/10 text-brand"
                      : "text-slate-400 hover:bg-white/5 hover:text-slate-100"
                  }`
                }
              >
                <n.icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="border-t border-subtle p-4 font-mono text-[10px] leading-relaxed text-slate-600">
          Prototype · fictional / synthetic data only. Not a certification of compliance.
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex items-center justify-between gap-4 border-b border-subtle bg-surface/80 px-5 py-3 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-display text-sm font-bold text-white" data-testid="active-tenant-name">
                  {session.tenant.name}
                </span>
                <Pill tone="eu">{session.tenant.jurisdiction?.split("·").pop()?.trim()}</Pill>
              </div>
              <div className="font-mono text-[11px] uppercase tracking-widest text-slate-500">
                {session.tenant.sector} sector pack
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden items-center gap-2 sm:flex">
              <Circle className="h-2 w-2 fill-allow text-allow" />
              <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">
                Gateway online
              </span>
            </div>
            <div className="text-right">
              <div className="text-sm font-semibold text-white" data-testid="current-user-name">
                {session.user.name}
              </div>
              <div className="font-mono text-[11px] uppercase tracking-widest text-slate-500">
                {session.user.role}
                {session.department ? ` · ${session.department.name}` : ""}
              </div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={doLogout}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-subtle text-slate-400 transition-colors hover:border-block/40 hover:text-block"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>

        <main className="mx-auto w-full max-w-[1500px] flex-1 px-5 py-7">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
